import {
  maybeParseCalendarDate,
  parseZonedDateTime,
} from '@rematter/common/dateTime';
import { CurrencyCode, Weight } from '@rematter/frontend-lib-api-client';

import { enumLoadWeightType, LoadRecord } from '../types/load';
import { toMoney } from '../utils/money';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export const loadDetailQueryAtom = createQueryAtomFactory(
  'trade.loadDetail',
  (params: { id: string }) => ({
    load: {
      __args: {
        id: params.id,
      },
      id: true,
      version: true,
      status: true,
      currencyCode: true,
      expectedShipDate: true,
      description: true,
      shippedDate: true,
      loadReceivedDate: true,
      customerAccountId: true,
      customerAccountAddressId: true,
      customerAccount: {
        id: true,
        name: true,
      },
      createdByUser: {
        id: true,
        firstName: true,
        lastName: true,
      },
      salesOrderId: true,
      salesOrder: {
        id: true,
        documentNumber: true,
        counterpartyReferenceNumber: true,
        paymentTerm: true,
        account: {
          id: true,
          name: true,
        },
      },
      bookingId: true,
      booking: {
        id: true,
        documentNumber: true,
        status: true,
      },
      documentNumber: true,
      facilityId: true,
      carrierAccountId: true,
      freightTerms: true,
      shippingCarrierName: true,
      scac: true,
      freightForwarder: true,
      truckFreight: true,
      deliveryNumber: true,
      releaseNumber: true,
      driverName: true,
      truckNumber: true,
      trailerNumber: true,
      containerNumber: true,
      sealNumber: true,
      bolNumber: true,
      containerTypeId: true,
      containerType: {
        id: true,
        name: true,
      },
      container: {
        id: true,
        containerNumber: true,
      },
      outboundTollingAgreementId: true,
      receivingFacilityId: true,
      heavyWeight: {
        value: true,
        unit: true,
      },
      lightWeight: {
        value: true,
        unit: true,
      },
      weightDisplayUnit: true,
      recyclerInternalNotes: true,
      recyclerExternalNotes: true,
      customerInternalNotes: true,
      customerExternalNotes: true,
      reconciliation: {
        id: true,
        invoiceId: true,
      },
      reviewed: true,
      receivingTicketNumber: true,
      receivedTicketIssueDate: true,
      millReceivedWeightPostDate: true,
      lineItems: {
        id: true,
        loadId: true,
        type: true,
        itemId: true,
        item: {
          id: true,
          name: true,
          skuNumber: true,
        },
        tagId: true,
        salesOrderLineId: true,
        salesOrderLine: {
          id: true,
        },
        phase: true,
        unitPrice: {
          amount: true,
          currencyCode: true,
        },
        estimatedPrice: {
          amount: true,
          currencyCode: true,
        },
        pricingUnit: true,
        weights: true,
        notes: true,
      },
      timestamps: {
        createdAt: true,
        updatedAt: true,
      },
      subtotal: {
        amount: true,
        currencyCode: true,
      },
      deductionsTotal: {
        amount: true,
        currencyCode: true,
      },
      grandTotal: {
        amount: true,
        currencyCode: true,
      },
      billId: true,
      deductions: {
        id: true,
        loadId: true,
        deductionTypeId: true,
        deductionType: {
          id: true,
          name: true,
          applicationType: true,
        },
        applicationType: true,
        weightValue: {
          value: true,
          unit: true,
        },
        priceValue: {
          amount: true,
          currencyCode: true,
        },
        percentage: true,
        calculatedAmount: {
          amount: true,
          currencyCode: true,
        },
        notes: true,
        loadLineItemId: true,
      },
    },
  }),
  {
    select: (data): { load: LoadRecord | null } => {
      if (!data?.load) {
        return { load: null };
      }

      return {
        load: {
          ...data.load,
          currencyCode: data.load.currencyCode as CurrencyCode | undefined,
          // Map new field names to old field names for backward compatibility
          internalNotes: data.load.recyclerInternalNotes,
          externalNotes: data.load.recyclerExternalNotes,
          heavyWeight: data.load.heavyWeight
            ? {
                ...data.load.heavyWeight,
                __typename: 'Weight' as const,
              }
            : null,
          lightWeight: data.load.lightWeight
            ? {
                ...data.load.lightWeight,
                __typename: 'Weight' as const,
              }
            : null,
          expectedShipDate: maybeParseCalendarDate(data.load.expectedShipDate),
          shippedDate: data.load.shippedDate
            ? maybeParseCalendarDate(data.load.shippedDate)
            : undefined,
          loadReceivedDate: data.load.loadReceivedDate
            ? maybeParseCalendarDate(data.load.loadReceivedDate)
            : undefined,
          salesOrder: data.load.salesOrder
            ? {
                id: data.load.salesOrder.id,
                documentNumber: data.load.salesOrder.documentNumber,
                customerName: data.load.salesOrder.account.name ?? null,
                counterPartyPurchaseOrderNumber:
                  data.load.salesOrder.counterpartyReferenceNumber ?? null,
                paymentTerm: data.load.salesOrder.paymentTerm ?? undefined,
              }
            : undefined,
          salesOrderNumber: data.load.salesOrder?.documentNumber,
          loadNumber: data.load.documentNumber.toString(),
          lineItemCount: data.load.lineItems.length,
          // Transform lineItems to match the structure expected by LoadStatusTransition
          lineItems: data.load.lineItems.map((lineItem) => ({
            id: lineItem.id,
            loadId: lineItem.loadId,
            type: lineItem.type,
            itemId: lineItem.itemId,
            item: lineItem.item ?? null,
            tagId: lineItem.tagId,
            unitPrice: lineItem.unitPrice ? toMoney(lineItem.unitPrice) : null,
            estimatedPrice: lineItem.estimatedPrice
              ? toMoney(lineItem.estimatedPrice)
              : null,
            pricingUnit: lineItem.pricingUnit,
            phase: lineItem.phase,
            weights: lineItem.weights as Record<string, Weight>,
            notes: lineItem.notes ?? null,
          })),
          netRequestedQuantity: data.load.lineItems.reduce(
            (acc, lineItem) =>
              acc +
              ((lineItem.weights as Record<string, Weight>)?.[
                enumLoadWeightType.Net
              ]?.value ?? 0),
            0,
          ),
          netOwedQuantity: data.load.lineItems.reduce(
            (acc, lineItem) =>
              acc +
              ((lineItem.weights as Record<string, Weight>)?.[
                enumLoadWeightType.QuantityOwed
              ]?.value ?? 0),
            0,
          ),
          netQuantity: data.load.lineItems.reduce(
            (acc, lineItem) =>
              acc +
              ((lineItem.weights as Record<string, Weight>)?.[
                enumLoadWeightType.Net
              ]?.value ?? 0),
            0,
          ),
          customerAccountId: data.load.customerAccountId ?? undefined,
          customerName: data.load.customerAccount?.name ?? undefined,
          salesOrderId: data.load.salesOrderId ?? undefined,
          weightDisplayUnit: data.load.weightDisplayUnit,
          containerTypeId: data.load.containerTypeId ?? undefined,
          containerType: data.load.containerType ?? undefined,
          containerId: data.load.container?.id ?? undefined,
          container: data.load.container ?? undefined,
          outboundTollingAgreementId:
            data.load.outboundTollingAgreementId ?? undefined,
          receivingFacilityId: data.load.receivingFacilityId ?? undefined,
          bookingId: data.load.bookingId ?? undefined,
          bookingNumber: data.load.booking?.documentNumber ?? undefined,
          bookingStatus: data.load.booking?.status ?? undefined,
          accountId: data.load.salesOrder?.account?.id,
          invoiceId: data.load.reconciliation?.invoiceId ?? undefined,
          reconciliation: null,
          reviewed: data.load.reviewed ?? false,
          files: [],
          receivedTicketIssueDate: data.load.receivedTicketIssueDate
            ? maybeParseCalendarDate(data.load.receivedTicketIssueDate)
            : null,
          millReceivedWeightPostDate: data.load.millReceivedWeightPostDate
            ? maybeParseCalendarDate(data.load.millReceivedWeightPostDate)
            : null,
          timestamps: {
            createdAt: parseZonedDateTime(data.load.timestamps.createdAt),
            updatedAt: parseZonedDateTime(data.load.timestamps.updatedAt),
          },
          subtotal: data.load.subtotal ? toMoney(data.load.subtotal) : null,
          deductionsTotal: data.load.deductionsTotal
            ? toMoney(data.load.deductionsTotal)
            : null,
          grandTotal: data.load.grandTotal
            ? toMoney(data.load.grandTotal)
            : null,
          billId: data.load.billId ?? null,
          deductions: data.load.deductions.map((d) => ({
            id: d.id,
            loadId: d.loadId,
            deductionTypeId: d.deductionTypeId,
            deductionType: d.deductionType ?? null,
            applicationType: d.applicationType,
            weightValue: d.weightValue
              ? { ...d.weightValue, __typename: 'Weight' as const }
              : null,
            priceValue: d.priceValue ? toMoney(d.priceValue) : null,
            percentage: d.percentage ?? null,
            calculatedAmount: d.calculatedAmount
              ? toMoney(d.calculatedAmount)
              : null,
            notes: d.notes ?? null,
            loadLineItemId: d.loadLineItemId ?? null,
          })),
        },
      };
    },
  },
);
